<script lang="ts">
	import { PUBLIC_BASE_URL } from '$env/static/public';
	import { browser } from '$app/environment';
	import { GtmStore } from '$lib/utils/gtm-store';
	import { goto } from '$app/navigation';
	import { EventUtils, useEventUtils } from '$lib/utils/event-utils';
	import { StructureUtils } from '$lib/utils/structured-utils';
	import type { IInitiativeFields, IStaticPage } from '$lib/utils/contentful/types';
	import type { PageData } from './$types';
	import { page } from '$app/stores';
	// Components
	import { Head } from '$lib/components/atoms/Head';
	import { Breadcrumb, BreadcrumbItem } from '$lib/components/molecules/Breadcrumb';
	import { HeroStandard } from '$lib/components/organisms/Hero';
	import { Section, SimpleCardsGrid } from '$lib/components/organisms/Section';
	import { Grid } from '$lib/components/organisms/Layout';
	import { Filter, FilterDropdown } from '$lib/components/molecules/Filter';
	import { Checkbox } from '$lib/components/atoms/Field';
	import { EventCard } from '$lib/components/organisms/Card';
	import { LoadMore } from '$lib/components/atoms/LoadMore';

	export let data: PageData;

	const eventUtils = useEventUtils(fetch);

	let filters = [];

	// closer initiatives pagination
	let closerInitiativesList: IInitiativeFields[] = [];
	let closerPage = 0;
	let closerHasMore = true;
	const closerPageSize = 27;
	let closerIsLoading = false;

	// past initiatives pagination
	let pastInitiativesList: IInitiativeFields[] = [];
	let pastPage = 0;
	let pastHasMore = true;
	const pastPageSize = 3;
	let pastIsLoading = false;

	const eventTypes = [
		{
			name: 'Open Lesson',
			selected: false
		},
		{
			name: 'Open Day',
			selected: false
		},
		{
			name: 'Selezioni',
			selected: false
		},
		{
			name: 'Happy Hour 24',
			selected: false
		},
		{
			name: 'Webinar',
			selected: false
		},
		{
			name: 'Evento Fisico',
			selected: false
		}
	];

	let eventTypesFrontend = eventTypes;

	const getMoreCloserInitiatives = async () => {
		closerIsLoading = true;
		try {
			const { initiatives, total } = await eventUtils.getCloserInitiatives(
				closerPage,
				closerPageSize,
				filters
			);
			closerInitiativesList = [...closerInitiativesList, ...initiatives];

			if (closerInitiativesList.length < total) {
				closerHasMore = true;
			} else {
				closerHasMore = false;
			}
			closerPage++;

			return initiatives;
		} catch (e) {
			closerHasMore = false;
		} finally {
			closerIsLoading = false;
		}
	};

	const getMorePastInitiatives = async () => {
		pastIsLoading = true;
		try {
			const { initiatives, total } = await eventUtils.getPastInitiatives(
				pastPage,
				pastPageSize,
				filters
			);
			pastInitiativesList = [...pastInitiativesList, ...initiatives];

			if (pastInitiativesList.length < total) {
				pastHasMore = true;
			} else {
				pastHasMore = false;
			}
			pastPage++;

			return initiatives;
		} catch (e) {
			pastHasMore = false;
		} finally {
			pastIsLoading = false;
		}
	};

	const queryFilters = () => {
		closerPage = 0;
		pastPage = 0;
		eventTypes.forEach((event) => {
			event.selected = false;
		});

		if (browser && $page.url.searchParams.has('filters')) {
			let query = $page.url.searchParams.get('filters');

			if (query && query.split(',').length > 0) {
				filters = query.split(',');
				filters.forEach((filter) => {
					let index = eventTypes.findIndex((item: any) => item.name == filter);
					if (index != -1) {
						eventTypes[index].selected = true;
					}
				});
			} else {
				filters = [];
			}
		} else {
      filters = [];
    }

		return eventTypes;
	};

	const changeHandler = async (e) => {

		let activeFilters = [];
		const selectedFilters = e.detail.data;

		for (const filter in selectedFilters) {
			if (selectedFilters[filter]) {
				activeFilters.push(filter);
			}
		}

    if (browser) {
			const baseUrl = $page.url.pathname;
			const searchParams = $page.url.searchParams;

			if (activeFilters.length > 0) {
				searchParams.set('filters', activeFilters.join(','));
			} else {
				searchParams.delete('filters');
			}

			const queryString = searchParams.toString() !== '' ? '?' + searchParams.toString() : '';
			const url = baseUrl + queryString;

      goto(url, { noScroll: true, invalidateAll: true });
		}
	};

	const applyFilters = async () => {
		try {
			closerInitiativesList = await getMoreCloserInitiatives();
			pastInitiativesList = await getMorePastInitiatives();
		} catch (error) {
			console.log(error);
		}
	};

	/******************** GTM EVENT PUSH *************************/
	GtmStore.pushEvent({
		pageCategory: 'Network - Iniziative'
	});
	/*************************************************************/

	const breadcrumbJson = StructureUtils.getBreadcrumbStructure(
		[
			{
				name: 'Home',
				url: PUBLIC_BASE_URL
			},
			{
				name: 'Iniziative',
				url: `${PUBLIC_BASE_URL}eventi`
			}
		],
		true
	);

	let ldScriptTag = '';
	ldScriptTag = StructureUtils.createScriptTag(JSON.stringify([breadcrumbJson]));

	const metaTagData: IStaticPage | null = data?.metaTagData ? JSON.parse(data.metaTagData) : null;

	const seoDetails = {
		title: metaTagData?.fields?.metaTagSocialTitle || 'Open Day, Live Streaming e webinar',
		description:
			metaTagData?.fields?.metaTagSocialDescription ||
			'Scopri le iniziative di 24ORE Business School, Open Day, Live Streaming e Webinar e rimani in contatto con noi.',
		metaTagSocialTitle: metaTagData?.fields?.metaTagSocialTitle || '',
		metaTagSocialDescription: metaTagData?.fields?.metaTagSocialDescription || '',
		url: '/eventi',
		image: metaTagData?.fields?.metaTagSocialImage?.fields.file.url || '',
		metaTagSocialImage: metaTagData?.fields?.metaTagSocialImage?.fields.file.url || '',
		ldScriptTag: ldScriptTag
	};

	$: if ($page.url.pathname) {
		eventTypesFrontend = queryFilters();
    applyFilters();
	}
</script>

<Head appendCompany {seoDetails} />

<Breadcrumb disableLastElementLink>
	<BreadcrumbItem>
		<a href="/">Home</a>
	</BreadcrumbItem>
	<BreadcrumbItem current>
		<a href="/eventi">Iniziative</a>
	</BreadcrumbItem>
</Breadcrumb>

<HeroStandard
	data={{
		image: '/assets/placeholders/mobile/hero-iniziative.webp',
		srcset:
			'/assets/placeholders/desktop/hero-iniziative.webp 1x, /assets/placeholders/high/hero-iniziative.webp 2x',
		imageAlt: 'Schermo con meeting online in corso',
		subtitle: 'Iniziative',
		title: seoDetails.title.slice(0, 60)
	}}
/>

<Section class="bg-white" noPaddingBottom noPaddingTop>
	<Grid class="py-6">
		<div class="col-span-full">
			<Filter on:change={changeHandler}>
				<FilterDropdown title="Tipologia">
					{#each eventTypesFrontend as type, i}
						<Checkbox id="tipologia-{i}" name={type.name} value={true} checked={type.selected}>
							{EventUtils.initiativeTypeEvent(type.name)}
						</Checkbox>
					{/each}
				</FilterDropdown>
			</Filter>
		</div>
	</Grid>
</Section>

{#if closerInitiativesList.length > 0}
	<Section compact class="bg-gray-50">
		<svelte:fragment slot="header">
			<h2 class="fs-3">Prossime iniziative</h2>
		</svelte:fragment>

		<Grid>
			<SimpleCardsGrid>
				{#each closerInitiativesList as event}
					{#if !event.fields.hideInitiativeFromWebsiteAndSearch && !event.fields.hideInitiativeFromWebsiteAndSearch == true}
						<EventCard {event} />
					{/if}
				{/each}
			</SimpleCardsGrid>
		</Grid>

		<div class="mt-16" slot="footer">
			{#if closerHasMore}
				<LoadMore
					isLoading={closerIsLoading}
					label="Altri risultati"
					on:click={getMoreCloserInitiatives}
				/>
			{/if}
		</div>
	</Section>
{/if}

{#if pastInitiativesList.length > 0}
  <Section noPaddingTop={closerInitiativesList.length > 0} compact class="bg-gray-50">
    <svelte:fragment slot="header">
      <h2 class="fs-3">Iniziative passate</h2>
    </svelte:fragment>

    <Grid>
      <SimpleCardsGrid>
        {#each pastInitiativesList as event}
          {#if !event.fields.hideInitiativeFromWebsiteAndSearch && !event.fields.hideInitiativeFromWebsiteAndSearch == true}
            <EventCard {event} />
          {/if}
        {/each}
      </SimpleCardsGrid>
    </Grid>

    <div class="mt-16" slot="footer">
      {#if pastHasMore}
        <LoadMore
          isLoading={pastIsLoading}
          label="Altri risultati"
          on:click={getMorePastInitiatives}
        />
      {/if}
    </div>
  </Section>
{/if}

{#if closerInitiativesList.length === 0 && pastInitiativesList.length === 0}
  <Section class="bg-gray-50">
    <Grid>
      <div class="col-span-full text-center">
        <p class="fs-3">Non ci sono iniziative disponibili</p>
      </div>
    </Grid>
  </Section>
{/if}

